import api from '../../utils/api'
import { useState, useEffect } from 'react'
import Select from 'react-select'
import { FiSend, FiCheckCircle, FiEye, FiInbox, FiDollarSign } from 'react-icons/fi'

import Button from '../../shared/components/Button/Button'
import Modal from '../../shared/modal/Modal'
import Input from '../../shared/components/form/input'
import selectStyles from '../../shared/css/SelectStyle'
import OpDetail from './OpDetail'
import ProductionReportIcon from '../../shared/components/ProductionReportIcon/ProductionReportIcon'

import useMyPermission from '../../hooks/useMyPermission'
import useFlashMessage from '../../hooks/useFlashMessage'
import { PRODUCTION_SECTORS } from '../../shared/constants/productionSectors'

import './Production.css'

// Mesmo helper de Production.js/OrderReport.js/Purchase.js — resume o item num texto curto.
function descriptionText(item) {
    const parts = []
    if (item.category) parts.push(item.category.label || item.category.name)
    if (item.cloth) parts.push(item.cloth.label || item.cloth.name)
    if (item.sex) parts.push(item.sex.label)
    return parts.filter(Boolean).join(' · ')
}

function itemLabel(item) {
    if (!item) return 'Item'
    return item.simpleProduct ? item.description : (descriptionText(item) || item.description || 'Item')
}

// Mesmo helper de Production.js — tempo desde o último evento do pacote.
function timeSince(pkg) {
    const last = pkg.status === 'em_transito' ? pkg.sentAt : (pkg.receivedAt || pkg.sentAt)
    const hours = (Date.now() - new Date(last).getTime()) / (1000 * 60 * 60)
    const cls = hours < 24 ? 'ok' : hours < 72 ? 'warn' : 'crit'
    const text = hours < 24 ? `há ${Math.max(1, Math.round(hours))}h` : `há ${Math.round(hours / 24)}d`
    return { text, cls }
}

// Fila operacional de um setor — mesmo board de Produção, filtrado por setor (?sector=) e
// mostrado como lista (não board em colunas), com as mesmas 4 ações por pacote que já existem
// no board geral. Extraído de Cut.js (que virou um wrapper fino deste componente) pra reusar em
// Costura/Acabamento/Inspeção/Expedição/Facção Externa sem duplicar a lógica.
function SectorQueue({ sector }) {
    const [token] = useState(localStorage.getItem('token') || '')
    const { setFlashMessage } = useFlashMessage()

    const [load, setLoad] = useState(false)
    const [activePackages, setActivePackages] = useState([])

    const [receiveItem, setReceiveItem] = useState(null)
    const [receiveQuantity, setReceiveQuantity] = useState('')
    const [receiveObs, setReceiveObs] = useState('')

    const [sendItem, setSendItem] = useState(null)
    const [sendSector, setSendSector] = useState(null)
    const [sendQuantity, setSendQuantity] = useState('')
    const [sendObs, setSendObs] = useState('')

    const [costItem, setCostItem] = useState(null)
    const [costDescription, setCostDescription] = useState('')
    const [costValue, setCostValue] = useState('')

    const [opDetailId, setOpDetailId] = useState(null)

    /* ---------------------- Permission ------------------------- */
    const [myPermission, setMyPermission] = useState({ group: '' })
    const { checkPermission } = useMyPermission()
    const myPermissionView = checkPermission(myPermission, 'production_view')
    const myPermissionEdit = checkPermission(myPermission, 'production_edit')

    // Só o Corte converte material (kg/m) em peças — nos demais setores o pacote que chega já
    // está em unidade, então usa o rótulo genérico "Enviar" em vez de "Registrar Corte".
    const isCut = sector === 'Corte'

    const getAll = () => {
        api.get(`/production/board?sector=${encodeURIComponent(sector)}`, { headers: { Authorization: `Bearer ${JSON.parse(token)}` } })
            .then((response) => {
                setActivePackages(response.data.activePackages || [])
                setMyPermission(response.data.permission)
                setLoad(true)
            })
    }

    useEffect(() => {
        getAll()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [token, sector])

    function openReceive(pkg) {
        setReceiveItem(pkg)
        setReceiveQuantity(String(pkg.sentQuantity))
        setReceiveObs('')
    }

    async function submitReceive() {
        let msgType = 'success'
        const data = await api.patch(`/production/package/${receiveItem._id}/receive`, {
            receivedQuantity: parseFloat(receiveQuantity),
            obs: receiveObs,
        }, {
            headers: { Authorization: `Bearer ${JSON.parse(token)}` },
        }).then((response) => response.data).catch((err) => { msgType = 'error'; return err.response.data })

        setFlashMessage(data.message, msgType)
        if (msgType !== 'error') {
            setReceiveItem(null)
            getAll()
        }
    }

    function openSend(pkg) {
        setSendItem(pkg)
        setSendSector(null)
        setSendQuantity(pkg.unit?.value === 'un' ? String(pkg.remainingQuantity) : '')
        setSendObs('')
    }

    async function submitSend() {
        if (!sendSector) {
            setFlashMessage('Selecione o setor de destino', 'error')
            return
        }

        let msgType = 'success'
        const data = await api.patch(`/production/package/${sendItem._id}/send`, {
            toSector: sendSector.value,
            quantity: parseFloat(sendQuantity),
            obs: sendObs,
        }, {
            headers: { Authorization: `Bearer ${JSON.parse(token)}` },
        }).then((response) => response.data).catch((err) => { msgType = 'error'; return err.response.data })

        setFlashMessage(data.message, msgType)
        if (msgType !== 'error') {
            setSendItem(null)
            getAll()
        }
    }

    async function finishPackage(id) {
        let msgType = 'success'
        const data = await api.patch(`/production/package/${id}/finish`, {}, {
            headers: { Authorization: `Bearer ${JSON.parse(token)}` },
        }).then((response) => response.data).catch((err) => { msgType = 'error'; return err.response.data })

        setFlashMessage(data.message, msgType)
        if (msgType !== 'error') getAll()
    }

    function openCost(pkg) {
        setCostItem(pkg)
        setCostDescription('')
        setCostValue('')
    }

    async function submitCost() {
        if (!costDescription || !costValue) {
            setFlashMessage('Informe descrição e valor', 'error')
            return
        }

        let msgType = 'success'
        const data = await api.patch(`/production/op/${costItem.productionOrder._id}/addCost`, {
            description: costDescription,
            value: parseFloat(costValue),
            sector: costItem.toSector,
        }, {
            headers: { Authorization: `Bearer ${JSON.parse(token)}` },
        }).then((response) => response.data).catch((err) => { msgType = 'error'; return err.response.data })

        setFlashMessage(data.message, msgType)
        if (msgType !== 'error') {
            setCostItem(null)
            getAll()
        }
    }

    function renderRow(pkg) {
        const badge = timeSince(pkg)
        const po = pkg.productionOrder
        const displayQty = pkg.status === 'em_transito' ? pkg.sentQuantity : pkg.remainingQuantity
        return (
            <div key={pkg._id} className="pg-item">
                <div className="pg-item-main">
                    <div className="prod-queue-header">
                        <span className="prod-card-order">#{po?.order?.code} — {po?.order?.client?.name}</span>
                        <span className={`prod-card-time prod-card-time-${badge.cls}`}>{badge.text}</span>
                    </div>
                    <span className="prod-card-op">{po?.code} · {itemLabel(po?.itemSnapshot)}</span>
                    <span className="prod-card-qty">{displayQty} {pkg.unit?.label || pkg.unit?.value}</span>
                </div>

                <div className="prod-queue-status-col">
                    <span className="prod-queue-status-caption">Status</span>
                    <span className={`prod-card-status prod-card-status-${pkg.status}`}>{pkg.status === 'em_transito' ? 'Em trânsito' : 'Recebido'}</span>
                </div>

                <div className="shd-row-box-item-action">
                    <ProductionReportIcon title="Ver Dados do Pedido" onClick={() => window.open(`/order/report/${po?.order?._id}`, '_blank')} />
                    <FiEye className="shd-icon-row-edit" title="Ver Ordem de Produção" onClick={() => setOpDetailId(po?._id)} />
                    <FiDollarSign className="shd-icon-row-edit" title="Lançar Custo" onClick={() => openCost(pkg)} />
                    {myPermissionEdit && pkg.status === 'em_transito' && (
                        <FiInbox className="shd-icon-row-edit" title="Confirmar Recebimento" onClick={() => openReceive(pkg)} />
                    )}
                    {myPermissionEdit && pkg.status === 'recebido' && (
                        <>
                            <FiSend className="shd-icon-row-edit" title={isCut ? 'Registrar Corte / Enviar' : 'Enviar'} onClick={() => openSend(pkg)} />
                            <FiCheckCircle className="shd-icon-row-edit" title="Finalizar" onClick={() => finishPackage(pkg._id)} />
                        </>
                    )}
                </div>
            </div>
        )
    }

    if (!load) {
        return <div className="pg-loading">Carregando fila de {sector}...</div>
    }

    return myPermissionView ? (
        <section className="pg-page">

            <div className="pg-sticky-top">
                <div className="pg-header">
                    <div className="pg-header-title">
                        <p className="pg-title">{sector}</p>
                        <span className="pg-title-accent" />
                    </div>
                    <div className="pg-header-row">
                        <div className="pg-header-right">
                            <span className="pg-count">
                                {activePackages.length} {activePackages.length === 1 ? 'pacote na fila' : 'pacotes na fila'}
                            </span>
                        </div>
                    </div>
                </div>
            </div>

            <div className="pg-list">
                {activePackages.length > 0 ? (
                    activePackages.map(renderRow)
                ) : (
                    <div className="pg-empty">
                        <p className="pg-empty-title">Nenhum pacote em {sector}</p>
                        <p className="pg-empty-subtitle">
                            {isCut
                                ? 'Pacotes aparecem aqui assim que Compras enviar material pro Corte.'
                                : `Pacotes aparecem aqui assim que forem enviados para ${sector}.`}
                        </p>
                    </div>
                )}
            </div>

            <Modal
                modalStatus={!!receiveItem}
                changeStatus={() => setReceiveItem(null)}
                w={40}
                content={
                    receiveItem && (
                        <div className="prod-send">
                            <p className="prod-send-title">Confirmar Recebimento</p>
                            <span className="prod-send-title-accent" />

                            <Input text={`Quantidade recebida (enviado: ${receiveItem.sentQuantity} ${receiveItem.unit?.label || receiveItem.unit?.value})`} type="number" name="receiveQuantity" placeholder="0" handleOnChange={(e) => setReceiveQuantity(e.target.value)} value={receiveQuantity} />
                            <Input text="Observação" type="text" name="receiveObs" placeholder="Opcional" handleOnChange={(e) => setReceiveObs(e.target.value)} value={receiveObs} />

                            <div className="prod-send-actions">
                                <Button variant="cancel" onClick={() => setReceiveItem(null)}>Cancelar</Button>
                                <Button variant="save" onClick={submitReceive}>Confirmar</Button>
                            </div>
                        </div>
                    )
                }
            />

            <Modal
                modalStatus={!!sendItem}
                changeStatus={() => setSendItem(null)}
                w={40}
                content={
                    sendItem && (
                        <div className="prod-send">
                            <p className="prod-send-title">{isCut ? 'Registrar Corte' : 'Enviar Pacote'}</p>
                            <span className="prod-send-title-accent" />

                            <div className="form-group">
                                <label className="input-label">Setor de destino:</label>
                                <Select
                                    options={PRODUCTION_SECTORS}
                                    styles={selectStyles}
                                    value={sendSector}
                                    onChange={setSendSector}
                                    menuPortalTarget={document.body}
                                    menuPosition="fixed"
                                    placeholder="Selecione o setor..."
                                />
                            </div>

                            <Input
                                text={sendItem.unit?.value === 'un' ? `Quantidade (máx. ${sendItem.remainingQuantity})` : `Quantidade de peças cortadas (esperado: ${sendItem.productionOrder?.quantity ?? '?'})`}
                                type="number" name="sendQuantity" placeholder="0" handleOnChange={(e) => setSendQuantity(e.target.value)} value={sendQuantity}
                            />
                            <Input text="Observação" type="text" name="sendObs" placeholder="Opcional" handleOnChange={(e) => setSendObs(e.target.value)} value={sendObs} />

                            <div className="prod-send-actions">
                                <Button variant="cancel" onClick={() => setSendItem(null)}>Cancelar</Button>
                                <Button variant="save" onClick={submitSend}>Enviar</Button>
                            </div>
                        </div>
                    )
                }
            />

            <Modal
                modalStatus={!!costItem}
                changeStatus={() => setCostItem(null)}
                w={40}
                content={
                    costItem && (
                        <div className="prod-send">
                            <p className="prod-send-title">Lançar Custo — {costItem.toSector}</p>
                            <span className="prod-send-title-accent" />

                            <Input text="Descrição" type="text" name="costDescription" placeholder="Ex.: Linha de costura" handleOnChange={(e) => setCostDescription(e.target.value)} value={costDescription} />
                            <Input text="Valor (R$)" type="number" name="costValue" placeholder="0.00" handleOnChange={(e) => setCostValue(e.target.value)} value={costValue} />

                            <div className="prod-send-actions">
                                <Button variant="cancel" onClick={() => setCostItem(null)}>Cancelar</Button>
                                <Button variant="save" onClick={submitCost}>Lançar</Button>
                            </div>
                        </div>
                    )
                }
            />

            <Modal
                modalStatus={!!opDetailId}
                changeStatus={() => setOpDetailId(null)}
                w={65}
                content={
                    opDetailId && (
                        <OpDetail
                            productionOrderId={opDetailId}
                            myPermissionEdit={myPermissionEdit}
                            btCancelAction={() => setOpDetailId(null)}
                            handleOnChange={getAll}
                        />
                    )
                }
            />

        </section>
    ) : (
        <div className="pg-empty">
            <p className="pg-empty-title">Sem permissão para visualizar Produção</p>
        </div>
    )
}

export default SectorQueue
